import "./index.css"
import { isEmpty } from "lodash"
import { useState } from "react"

export interface SimpleTableProps {
  columns: {
    title: string
    dataIndex: string
    render?: (data: any) => any
    align?: "left" | "center" | "right"
    width?: string
    sortable?: boolean
  }[]
  dataSource: any
  displayOD?: boolean // ordinal numbers
  customClass?: string
  isRequiredTableHeader?: boolean
  onSort?: (dataIndex: string, sortDirection: "asc" | "desc" | null) => void
  onRowClick?: (data: any) => void
}

const Table = ({
  columns,
  dataSource,
  displayOD,
  customClass,
  isRequiredTableHeader = true,
  onSort,
  onRowClick,
}: SimpleTableProps) => {
  const [sortConfig, setSortConfig] = useState<{
    key: string | null
    direction: "asc" | "desc" | null
  }>({ key: null, direction: null })

  const handleSort = (dataIndex: string) => {
    let direction: "asc" | "desc" | null = "asc"

    if (sortConfig.key === dataIndex) {
      if (sortConfig.direction === "asc") {
        direction = "desc"
      } else if (sortConfig.direction === "desc") {
        direction = null
      }
    }

    setSortConfig({ key: direction ? dataIndex : null, direction })

    if (onSort) {
      onSort(dataIndex, direction)
    }
  }

  const getSortIcon = (dataIndex: string) => {
    if (sortConfig.key !== dataIndex) {
      return (
        <svg
          width="10"
          height="12"
          viewBox="0 0 10 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.9509 4.06204L6.53356 2.64469L5.66814 1.77486C5.30166 1.40838 4.70558 1.40838 4.3391 1.77486L2.05192 4.06204C1.75168 4.36229 1.96803 4.87447 2.3875 4.87447H4.86454H7.61533C8.03921 4.87447 8.25115 4.36229 7.9509 4.06204Z"
            fill="black"
            fill-opacity="0.25"
          />
          <path
            d="M7.61474 7.125H4.8628H2.38472C1.96067 7.125 1.74864 7.6374 2.04901 7.93777L4.33714 10.2259C4.70378 10.5925 5.3001 10.5925 5.66673 10.2259L6.53693 9.35571L7.95487 7.93777C8.25082 7.6374 8.03879 7.125 7.61474 7.125Z"
            fill="black"
            fill-opacity="0.25"
          />
        </svg>
      )
    }

    if (sortConfig.direction === "asc") {
      return (
        <svg
          width="10"
          height="12"
          viewBox="0 0 10 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.9509 4.06204L6.53356 2.64469L5.66814 1.77486C5.30166 1.40838 4.70558 1.40838 4.3391 1.77486L2.05192 4.06204C1.75168 4.36229 1.96803 4.87447 2.3875 4.87447H4.86454H7.61533C8.03921 4.87447 8.25115 4.36229 7.9509 4.06204Z"
            fill="#FD8A24"
          />
          <path
            d="M7.61474 7.125H4.8628H2.38472C1.96067 7.125 1.74864 7.6374 2.04901 7.93777L4.33714 10.2259C4.70378 10.5925 5.3001 10.5925 5.66673 10.2259L6.53693 9.35571L7.95487 7.93777C8.25082 7.6374 8.03879 7.125 7.61474 7.125Z"
            fill="black"
            fill-opacity="0.25"
          />
        </svg>
      )
    }

    if (sortConfig.direction === "desc") {
      return (
        <svg
          width="10"
          height="12"
          viewBox="0 0 10 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M7.9509 4.06204L6.53356 2.64469L5.66814 1.77486C5.30166 1.40838 4.70558 1.40838 4.3391 1.77486L2.05192 4.06204C1.75168 4.36229 1.96803 4.87447 2.3875 4.87447H4.86454H7.61533C8.03921 4.87447 8.25115 4.36229 7.9509 4.06204Z"
            fill="black"
            fill-opacity="0.25"
          />
          <path
            d="M7.61474 7.125H4.8628H2.38472C1.96067 7.125 1.74864 7.6374 2.04901 7.93777L4.33714 10.2259C4.70378 10.5925 5.3001 10.5925 5.66673 10.2259L6.53693 9.35571L7.95487 7.93777C8.25082 7.6374 8.03879 7.125 7.61474 7.125Z"
            fill="#FD8A24"
          />
        </svg>
      )
    }

    return null
  }
  return (
    <>
      <table
        className={`${
          customClass ? customClass : "simple-table"
        } w-full text-sm text-left text-gray-500 dark:text-gray-400`}
      >
        {isRequiredTableHeader && (
          <thead>
            <tr>
              {displayOD ? (
                <th className="text-center pr-[12px]">#</th>
              ) : undefined}
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`text-${column.align || "left"} ${
                    column.sortable
                      ? "cursor-pointer hover:bg-gray-50 select-none"
                      : ""
                  }`}
                  style={{
                    width: column.width || "auto",
                  }}
                  onClick={() =>
                    column.sortable && handleSort(column.dataIndex)
                  }
                >
                  <div className="flex items-center gap-[4px]">
                    {column.title}
                    {column.sortable && getSortIcon(column.dataIndex)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {dataSource
            .filter((data: any) => data.isShow !== false)
            .map((data: any, index: number) => (
              <tr
                className={`${
                  data.project_id ? "tr-secondary" : "tr-primary"
                } ${onRowClick ? "cursor-pointer hover:bg-gray-100" : ""} ${
                  !isRequiredTableHeader && index === 0
                    ? "first-row-no-border-top"
                    : ""
                }`}
                key={index}
                onClick={() => onRowClick && onRowClick(data)}
              >
                {displayOD ? (
                  <td className="text-center pr-[12px]">{index + 1}</td>
                ) : undefined}
                {columns.map((column, index2) => (
                  <td
                    key={index2}
                    className={`text-${column.align || "left"}`}
                    style={{
                      width: column.width || "auto",
                    }}
                  >
                    {column.render
                      ? column.render(data)
                      : data[column.dataIndex || ""]}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
      {isEmpty(dataSource) ? (
        <p className="text-center font-medium text-brown py-[10px]">
          No records found.
        </p>
      ) : null}
    </>
  )
}
export default Table
